import React from "react";
import styled from "@emotion/styled";
import { COLORS } from "../../utils/styles/colors";
import {
  ComponentLStyling,
  ComponentMStyling,
  ComponentSStyling,
  ComponentXSStyling,
  HeadlineLStyling,
  HeadlineMStyling,
  HeadlineXLStyling,
  ParagraphMStyling,
  ParagraphSStyling,
  TextStyle,
} from "../../utils/styles/typography";
import { BREAKPOINTS } from "../../utils/styles/breakpoints";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMediaMatch } from "rooks";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1350px;
  margin: 0 auto;
  padding: 0 16px;
  ${BREAKPOINTS.MEDIUM} {
    padding: 0 32px;
  }
  ${BREAKPOINTS.XLARGE} {
    padding: 0 56px;
  }
  margin-bottom: 56px;
`;

const BlocksSVGWrapper = styled.svg`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  border-radius: 25px;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 19px 38px,
    rgba(0, 0, 0, 0.22) 0px 15px 12px;
  margin-top: 25px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
  ${BREAKPOINTS.LARGE} {
    align-items: flex-start;
  }
`;

const Title = styled.div`
  ${HeadlineMStyling(TextStyle.SemiBold, COLORS.CustomSkin)}
  ${BREAKPOINTS.MEDIUM} {
    ${HeadlineLStyling(TextStyle.SemiBold, COLORS.CustomSkin)}
  }
  ${BREAKPOINTS.XLARGE} {
    ${HeadlineXLStyling(TextStyle.SemiBold, COLORS.CustomSkin)}
  }
`;

const Description = styled.div`
  margin-top: 10px;
  ${ComponentXSStyling(TextStyle.Regular, COLORS.White)}
  ${BREAKPOINTS.MEDIUM} {
    ${ComponentSStyling(TextStyle.Regular, COLORS.White)}
  }
`;

const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  order: 2;
  ${BREAKPOINTS.LARGE} {
    gap: 40px;
    flex: 1;
    order: 0;
  }
`;

const ProjectContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  ${BREAKPOINTS.LARGE} {
    align-items: flex-start;
    flex-direction: row;
  }
`;

const Subtitle = styled.div`
  ${ComponentMStyling(TextStyle.SemiBold, COLORS.CustomSkin)}
  ${BREAKPOINTS.XLARGE} {
    ${ComponentLStyling(TextStyle.SemiBold, COLORS.CustomSkin)}
  }
`;

const Paragraph = styled.div`
  ${ParagraphSStyling(TextStyle.Regular, COLORS.White)}
  max-width: 60ch;
  text-align: center;
  ${BREAKPOINTS.LARGE} {
    text-align: left;
  }
  ${BREAKPOINTS.LARGE} {
    ${ParagraphMStyling(TextStyle.Regular, COLORS.White)}
  }
`;

const ParagraphContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  ${BREAKPOINTS.LARGE} {
    align-items: flex-start;
  }
`;

interface BlockData {
  id: string;
  label: string;
  points: string;
  textPosition: { x: number; y: number };
}

export const BlocksPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isLargeScreen = useMediaMatch(BREAKPOINTS.LARGE.replace("@media ", ""));

  const handleBlockClick = (blockId: string) => {
    navigate(`/project/${blockId}`);
  };

  const blocks: BlockData[] = [
    {
      id: "lisi3",
      label: "მზადების პროცესში",
      points: "141,182,33,368,89,522,169,538,261,338,238,203", // Adjusted to match leftmost building
      textPosition: { x: 150, y: 300 },
    },
    {
      id: "lisi2",
      label: t("Block 2"),
      points: "299,325,282,170,415,46,497,85,488,225,365,362", // Adjusted to match top-right building
      textPosition: { x: 400, y: 160 },
    },
    {
      id: "lisi1",
      label: t("Block 1"),
      points: "534,453,551,317,679,159,774,205,727,354,617,500", // Adjusted to match lower-right building
      textPosition: { x: 650, y: 320 },
    },
  ];

  return (
    <Wrapper>
      <ContentContainer>
        {!isLargeScreen && (
          <Header>
            <Title>{t("შენობის გვერდი")}</Title>
            <Description>{t("აირჩიე სართული")}</Description>
          </Header>
        )}
        <ProjectContainer>
          <DescriptionContainer>
            {isLargeScreen && (
              <Header>
                <Title>{t("კორპუსის გვერდი")}</Title>
                <Description>{t("აირჩიე ბინა")}</Description>
              </Header>
            )}

            <ParagraphContainer>
              <Subtitle>{t("პროექტის შესახებ")}</Subtitle>
              <Paragraph>
                {t(
                  "Samgori Park Residence-ს არქიტექტურა დამყარებულია თანამედროვე ურბანული დიზაინის პრინციპებზე - მაქსიმალურად ათვისებული სივრცეები, მზის სხივზე ორიენტირებული ბინები და ვიზუალურად ჰარმონიული ბლოკები."
                )}
              </Paragraph>
              <Paragraph>● {t("სართულზე განთავსებულია 13 ბინა")}</Paragraph>
              <Paragraph>
                ● {t("ბინები დაპროექტებულია 56 კვ.მ-დან - 150 კვ.მ-მდე")}
              </Paragraph>
              <Paragraph>
                ●{" "}
                {t(
                  "გეგმარება განსაკუთრებულად პრაქტიკულია: ცალკე გამოყოფილი საძინებლები, ღია მისაღები ზონები, ბუნებრივი განათება და ვიტრაჟული ტიპის აივნები გამორჩეული ხედებით"
                )}
              </Paragraph>
            </ParagraphContainer>
          </DescriptionContainer>

          <BlocksSVGWrapper
            viewBox="0 0 800 700"
            preserveAspectRatio="xMidYMid meet"
          >
            <image
              href="/images/threeblocks.jpg"
              width="100%"
              height="100%"
              preserveAspectRatio="xMidYMid slice"
              style={{ pointerEvents: "none" }}
            />

            {blocks.map((block) => {
              const isDisabled = block.id === "lisi3";
              return (
                <React.Fragment key={block.id}>
                  <polygon
                    points={block.points}
                    fill="transparent"
                    stroke="transparent"
                    strokeWidth={4}
                    strokeLinejoin="round"
                    style={{
                      transition: "stroke 0.2s ease-out",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.setAttribute("stroke", COLORS.White);
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.setAttribute("stroke", "transparent");
                    }}
                    onClick={(e) => {
                      if (isDisabled) {
                        e.stopPropagation();
                        return;
                      }
                      handleBlockClick(block.id);
                    }}
                  >
                    <title>
                      {isDisabled ? "მზადების პროცესში" : block.label}
                    </title>
                  </polygon>
                  <text
                    x={block.textPosition.x}
                    y={block.textPosition.y}
                    fill={isDisabled ? "#ccc" : "white"}
                    textAnchor="middle"
                    fontSize={16}
                    fontWeight="bold"
                    opacity={0}
                    style={{
                      transition: "opacity 0.2s ease-out",
                      pointerEvents: "none",
                      textShadow: "0 1px 3px rgba(0,0,0,0.8)",
                    }}
                  >
                    {block.label}
                  </text>
                </React.Fragment>
              );
            })}

            <style>{`
              polygon:hover + text {
                opacity: 1;
              }
            `}</style>
          </BlocksSVGWrapper>
        </ProjectContainer>
      </ContentContainer>
    </Wrapper>
  );
};
